/**
 * TanStack Query hooks for the M365 service-health board.
 *
 * Both endpoints are backed by a server-side TTL cache over Microsoft Graph
 * (the browser never talks to Graph directly), so the poll interval here only
 * needs to be close to that TTL — polling harder just re-reads the same cached
 * snapshot.
 *
 * Neither endpoint fails when Graph is unconfigured or unreachable: it answers
 * 200 with `configured` / `status_reason` / `status_detail` set, and the UI
 * explains the gap. A thrown error therefore means the *NOVA* API is down, not
 * Microsoft — worth keeping in mind when reading the `error` branch.
 */

import { useQuery } from "@tanstack/react-query";

import { getM365Incidents, getM365ServiceHealth } from "@/lib/api/generated";
import { logger } from "@/lib/logger";

/** Query key for the full board payload. */
export const M365_SERVICE_HEALTH_KEY = ["m365", "service-health"] as const;
/** Query key for the incidents-only payload. */
export const M365_INCIDENTS_KEY = ["m365", "incidents"] as const;

/** Matches the backend's `CACHE_TTL_SEC` — refetching faster gains nothing. */
const REFETCH_INTERVAL_MS = 120_000;

export function useM365ServiceHealth() {
  return useQuery({
    queryKey: M365_SERVICE_HEALTH_KEY,
    queryFn: async () => {
      const { data } = await getM365ServiceHealth();
      logger.debug("[m365] service health received", {
        configured: data?.configured,
        statusReason: data?.status_reason,
        services: data?.services.length,
      });
      return data;
    },
    refetchInterval: REFETCH_INTERVAL_MS,
  });
}

export function useM365Incidents() {
  return useQuery({
    queryKey: M365_INCIDENTS_KEY,
    queryFn: async () => {
      const { data } = await getM365Incidents();
      logger.debug("[m365] incidents received", {
        active: data?.active_incidents.length,
        resolved: data?.resolved_recently.length,
      });
      return data;
    },
    refetchInterval: REFETCH_INTERVAL_MS,
  });
}
